import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../api/client';
import { useAuth } from '../../contexts/ClerkAuthContext';
import { GooglePicker } from './GooglePicker';
import LinearLogo from '../../assets/images/linear-logo.svg';
import GoogleSheetsLogo from '../../assets/images/googlesheets-logo.svg';
import AttioLogo from '../../assets/images/attio-logo.svg';

interface Integration {
  id: string;
  service_name: string;
  client_id: string;
  auth_url: string;
  redirect_uri: string;
  scopes: string[];
  additional_params: Record<string, any>;
  created: string;
  updated: string;
  connected: boolean;
}

export const SettingsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const apiClient = useApiClient();
  const { internalOrgId } = useAuth();

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoading(true);
        const headers: Record<string, string> = {};
        
        // Include Organization-ID header if internal org ID is available
        if (internalOrgId) {
          headers['Organization-ID'] = internalOrgId;
        }
        
        const data = await apiClient.get<Integration[]>('/api/integrations', headers);
        // Sort integrations by service name alphabetically
        const sortedData = data.sort((a, b) => a.service_name.localeCompare(b.service_name));
        setIntegrations(sortedData);
      } catch (err) {
        setError('Failed to load integrations');
        console.error('Error fetching integrations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [apiClient, internalOrgId]);

  // Handle selecting files for Google Sheets
  const handleSelectFiles = async (integration: Integration) => {
    try {
      const headers: Record<string, string> = {};
      if (internalOrgId) {
        headers['Organization-ID'] = internalOrgId;
      }
      
      // Get the access token from backend
      const tokenResponse = await apiClient.get<{access_token: string}>(`/api/integrations/${integration.service_name}/token`, headers);
      
      if (tokenResponse.access_token) {
        console.log('Got Google access token, showing picker...');
        setGoogleAccessToken(tokenResponse.access_token);
        setGoogleClientId(integration.client_id);
        setShowPicker(true);
      } else {
        alert('Failed to get access token. Please try reconnecting.');
      }
    } catch (err) {
      console.error('Failed to get Google token:', err);
      alert('Failed to load file picker. Please try reconnecting.');
    }
  };

  const buildOAuthUrl = async (integration: Integration): Promise<string> => {
    // Get signed state from backend
    const stateResponse = await apiClient.post<{state: string}>('/api/oauth/state', {
      integration_name: integration.service_name
    });

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: integration.client_id,
      redirect_uri: integration.redirect_uri,
      state: stateResponse.state,
      scope: integration.scopes.join(' ')
    });

    // Add any additional parameters from the integration
    Object.entries(integration.additional_params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, String(value));
      }
    });

    return `${integration.auth_url}?${params.toString()}`;
  };

  const handleConnect = async (integration: Integration) => {
    try {
      const oauthUrl = await buildOAuthUrl(integration);
      console.log('Redirecting to OAuth URL:', oauthUrl);
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Failed to generate OAuth URL:', error);
    }
  };

  const handleRevoke = async (integration: Integration) => {
    if (!window.confirm(`Are you sure you want to revoke access to ${integration.service_name}?`)) {
      return;
    }

    try {
      const headers: Record<string, string> = {};
      if (internalOrgId) {
        headers['Organization-ID'] = internalOrgId;
      }
      
      await apiClient.delete(`/api/integrations/${integration.service_name}`, headers);
      
      // Update the local state to reflect the disconnection
      setIntegrations(prevIntegrations => 
        prevIntegrations.map(i => 
          i.service_name === integration.service_name ? { ...i, connected: false } : i
        )
      );
    } catch (error) {
      console.error('Failed to revoke integration:', error);
      alert('Failed to revoke integration. Please try again.');
    }
  };

  const getIntegrationIcon = (serviceName: string) => {
    const iconMap: { [key: string]: string } = {
      linear: LinearLogo,
      googlesheets: GoogleSheetsLogo,
      attio: AttioLogo,
    };

    const iconSrc = iconMap[serviceName.toLowerCase()];
    
    if (iconSrc) {
      return (
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 p-1.5">
          <img 
            src={iconSrc} 
            alt={`${serviceName} logo`}
            className="w-full h-full object-contain"
          />
        </div>
      );
    }

    // Fallback to placeholder for unknown services
    return (
      <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
        <span className="text-sm font-bold text-gray-700 uppercase">
          {serviceName.charAt(0)}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="mt-2 text-gray-600">Manage your integrations and connect services to enhance your AI applications</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Integrations</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                      <div className="ml-3 flex-1">
                        <div className="h-5 bg-gray-200 rounded w-24 mb-2"></div>
                        <div className="h-3 bg-gray-100 rounded w-20"></div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <div className="h-10 bg-gray-200 rounded-lg"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-8">
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p className="text-gray-900 font-medium">{error}</p>
                <p className="text-gray-500 text-sm mt-2">Please try refreshing the page</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">Manage your integrations and connect services to enhance your AI applications</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Integrations</h2>
            
            {integrations.length === 0 ? (
              <div className="text-center py-12 px-4 bg-gray-50 rounded-lg">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="text-gray-500 font-medium">No integrations available</p>
                <p className="text-gray-400 text-sm mt-1">Check back later for new integration options</p>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {integrations.map((integration) => (
                  <div
                    key={integration.id}
                    className="group relative bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-gray-300 transition-all duration-200"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          {getIntegrationIcon(integration.service_name)}
                          <div className="ml-3">
                            <h3 className="font-semibold text-gray-900 capitalize text-lg">
                              {integration.service_name}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {integration.service_name === 'googlesheets' 
                                ? 'File-specific access' 
                                : integration.scopes.length === 1 
                                  ? integration.scopes[0] 
                                  : `Read / write permissions`}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-auto pt-4 border-t border-gray-100">
                        {integration.connected ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center text-emerald-600 bg-emerald-50 py-2 px-3 rounded-lg">
                              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm font-medium">Connected</span>
                            </div>
                            <div className="flex gap-2">
                              {integration.service_name === 'googlesheets' && (
                                <button
                                  onClick={() => handleSelectFiles(integration)}
                                  className="flex-1 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-150"
                                >
                                  Select Files
                                </button>
                              )}
                              <button
                                onClick={() => handleRevoke(integration)}
                                className={`${integration.service_name === 'googlesheets' ? 'flex-1' : 'w-full'} text-gray-700 bg-white hover:bg-gray-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 border border-gray-200`}
                              >
                                Disconnect
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center text-gray-500 bg-gray-50 py-2 px-3 rounded-lg">
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              <span className="text-sm font-medium">Not Connected</span>
                            </div>
                            <button
                              onClick={() => handleConnect(integration)}
                              className="w-full bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-150"
                            >
                              Connect
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Google Picker Modal */}
      {showPicker && googleAccessToken && googleClientId && (
        <GooglePicker
          accessToken={googleAccessToken}
          clientId={googleClientId}
          onSelect={(files) => {
            console.log('Selected files:', files);
            const count = files.length;
            setSuccessMessage(`Successfully granted access to ${count} spreadsheet${count !== 1 ? 's' : ''}`);
            setShowPicker(false);
            setGoogleAccessToken(null);
            setGoogleClientId(null);
            
            // Auto-hide success message after 5 seconds
            setTimeout(() => setSuccessMessage(null), 5000);
          }}
          onCancel={() => {
            console.log('Picker cancelled');
            setShowPicker(false);
            setGoogleAccessToken(null);
            setGoogleClientId(null);
          }}
        />
      )}
      
      {/* Success Toast - positioned below nav, above content */}
      {successMessage && (
        <div className="fixed top-16 left-0 right-0 bg-green-600 text-white px-6 py-3 shadow-lg flex items-center justify-center space-x-3 z-50">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-4 hover:opacity-75"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};